import { Link } from 'react-router-dom';
import { Avatar, Tooltip, Typography } from 'antd';
import { ApartmentOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DealItem } from '@crm/shared';
import { dealContactName, money } from '../../features/deals/api';
import { palette } from '../../app/theme';

const initials = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/** Board card: white, hairline border, title, organization, value and the contact's avatar. */
export function DealCard({ deal, dragging }: { deal: DealItem; dragging?: boolean }) {
  const overdue = deal.expectedCloseAt && deal.stage.kind === 'OPEN' && dayjs(deal.expectedCloseAt).isBefore(dayjs(), 'day');
  const contactName = deal.contact ? dealContactName(deal.contact) : null;
  return (
    <div
      style={{
        background: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: dragging ? '0 12px 28px rgba(17, 24, 39, 0.14)' : '0 1px 2px rgba(17, 24, 39, 0.03)',
        cursor: 'grab',
      }}
    >
      <Link to={`/deals/${deal.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600, color: palette.text, display: 'block', lineHeight: 1.35 }}>
        {deal.title}
      </Link>
      {deal.organization && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }} ellipsis>
          <ApartmentOutlined /> {deal.organization.name}
        </Typography.Text>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <Typography.Text strong style={{ fontSize: 15 }}>{money(deal.value, deal.currency)}</Typography.Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {deal.expectedCloseAt && (
            <Tooltip title={overdue ? 'Expected close date has passed' : 'Expected close'}>
              <Typography.Text type={overdue ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                <CalendarOutlined /> {dayjs(deal.expectedCloseAt).format('MMM D')}
              </Typography.Text>
            </Tooltip>
          )}
          <Avatar.Group size={24} max={{ count: 2 }}>
            {contactName && (
              <Tooltip title={contactName}>
                <Avatar size={24} style={{ background: palette.primarySoft, color: palette.primary, fontSize: 10, fontWeight: 600 }}>{initials(contactName)}</Avatar>
              </Tooltip>
            )}
            {deal.agent && (
              <Tooltip title={`Agent: ${deal.agent.name}`}>
                <Avatar size={24} src={deal.agent.avatarUrl ?? undefined} style={{ background: '#F3E8FF', color: '#7C3AED', fontSize: 10, fontWeight: 600 }}>{initials(deal.agent.name)}</Avatar>
              </Tooltip>
            )}
          </Avatar.Group>
        </div>
      </div>
    </div>
  );
}
